import React, { useMemo, useState, useEffect, useRef } from 'react';
import { prizeModalScrollSettings, shelfContent } from '../../../../constants/copyright';
import { useRequests } from '../../../../redux/reducer/requests';
import { Scrollbar } from 'react-scrollbars-custom';
import { useProducts } from '../../../../redux/reducer/content';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useRequestsHandlerData } from '../../../../hooks/useRequestsHandlerData';
import AppLoader from '../../../features/appLoader/AppLoader';
import ShelfInfo from '../shelfInfo/ShelfInfo';
import { languages } from '../../../../translations';
import { useLanguageControls } from '../../../../hooks/useLanguageControls';
import { useGetProductsAndClear } from '../../../features/product';
import ShelfHeader from '../shelfHeader/ShelfHeader';
import ShelfToggle from '../shelfToggle/ShelfToggle';
import ShelfItems from '../shelfItems/ShelfItems';

const { info } = shelfContent;

const Shelf = React.forwardRef(function ({ callbacks }, ref) {
  const {
    authData: { isWeb },
  } = useRequests();

  const { buyProducts, buyProductsGroups, productsFromGA } = useProducts();
  const { language } = useLanguageControls();
  const [getProducts, isPendingProducts] = useGetProductsAndClear();

  const toggleList = useMemo(() => {
    const ALLOWED_GROUPS = [
      'Волосы',
      'Макияж',
      'Парфюм',
      'Уход',
      'Шаш',
      'Қамқорлық',
      'Иіспа',
      'Әшекей',
      'Haircare',
      'Makeup',
      'Fragrance',
      'Skincare',
      'العناية بالشعر',
      'المكياج',
      'العطور',
      'العناية بالبشرة',
    ];
    const allowedGroupsLower = new Set(ALLOWED_GROUPS.map((group) => group.toLowerCase()));
    return buyProductsGroups
      .filter((group) => allowedGroupsLower.has(group?.toLowerCase()))
      .map((group, index) => ({
        id: group,
        text: group,
        action: `action-${++index}`,
      }));
  }, [buyProductsGroups]);

  const [toggle, setToggle] = useState(toggleList[0]?.action ?? 'action-1');

  const productList = useMemo(() => {
    const necessaryAction = toggleList?.find(({ action }) => action === toggle);
    const filterFunction =
      {
        [languages.kz]: ({ group_kz }) => group_kz === necessaryAction?.id,
        [languages.en]: ({ group_en }) => group_en === necessaryAction?.id,
        [languages.ar]: ({ group_ar }) => group_ar === necessaryAction?.id,
      }[language] ?? (({ group }) => group === necessaryAction?.id);
    return buyProducts.filter(filterFunction);
  }, [buyProducts, toggleList, toggle, language]);

  // TODO: прибраться

  // Делаем ключ для SKU, чтобы не триггерить загрузку на локальные обновления товаров (например, favorite)
  const productSkus = useMemo(() => productList.map((prod) => prod.sku).filter(Boolean), [productList]);
  const productSkusKey = useMemo(() => productSkus.join(','), [productSkus]);

  const [isLocalProductsLoading, setIsLocalProductsLoading] = useState(false);
  const loadedForSkusKeyRef = useRef(null);

  const isRequestProductsLoading = useRequestsHandlerData([
    {
      address: 'requestsGetWebCatalogProductsBySku',
      pending: () => setIsLocalProductsLoading(true),
      fulfilled: () => setIsLocalProductsLoading(false),
      rejected: () => setIsLocalProductsLoading(false),
      settled: () => setIsLocalProductsLoading(false),
    },
  ]);

  // При смене таба очищаем товары и начинаем загрузку
  useEffect(() => {
    if (!productSkus.length) return;

    loadedForSkusKeyRef.current = productSkusKey;
    setIsLocalProductsLoading(true);
    getProducts(productSkus);
  }, [toggle, productSkusKey, callbacks]); // productSkusKey фиксирует состав sku

  // При первой загрузке компонента загружаем товары (не повторяем запрос, если для этого productSkusKey уже был ответ — иначе цикл при пустом productsFromGA)
  useEffect(() => {
    if (
      productSkus.length > 0 &&
      !productsFromGA?.length &&
      !isRequestProductsLoading &&
      loadedForSkusKeyRef.current !== productSkusKey
    ) {
      loadedForSkusKeyRef.current = productSkusKey;
      setIsLocalProductsLoading(true);
      getProducts(productSkus);
    }
  }, [productSkusKey, productsFromGA, isRequestProductsLoading, callbacks]); // productSkusKey фиксирует состав sku

  const shelfCallbacks = useMemo(
    () =>
      toggleList.reduce(
        (acc, { action }) => ({
          ...acc,
          [action]: () => setToggle(action),
        }),
        {},
      ),
    [toggleList],
  );

  const isPending = useRequestsHandlerData([
    { address: 'requestsProductToFavorites' },
    { address: 'requestsUserServiceDataSave' },
  ]);

  const isProductsLoading = isRequestProductsLoading || isLocalProductsLoading;
  const shouldShowProducts = !isProductsLoading;

  return (
    <section className={`shelf ${isWeb ? '' : 'app'}`} ref={ref}>
      <ShelfHeader />
      <div className={'shelf__content'}>
        <ShelfToggle toggleList={toggleList} shelfCallbacks={shelfCallbacks} />
        <div className={'shelf__list'}>
          <Scrollbar {...prizeModalScrollSettings}>
            {info.map((item, index) => (
              <ShelfInfo key={`ShelfInfo-item-${index}`} {...item} />
            ))}
            {shouldShowProducts && (
              <SwitchTransition>
                <CSSTransition
                  classNames={'shelf__items'}
                  key={toggle}
                  appear={true}
                  timeout={300}
                  mountOnEnter={true}
                  unmountOnExit={true}
                >
                  <ShelfItems />
                </CSSTransition>
              </SwitchTransition>
            )}
            {isProductsLoading && (
              <div className={'shelf__items-loader'}>
                <span className={'shelf__items-loader-spinner'} />
              </div>
            )}
          </Scrollbar>
        </div>
        {isPending && <AppLoader />}
      </div>
    </section>
  );
});
export default Shelf;
Shelf.propTypes = {};
